import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Lock, Scale } from "lucide-react";

export const Logo = ({ className = "h-14", to = "/" }) => (
  <Link to={to} data-testid="brand-logo-link" className="shrink-0" aria-label="CRBETS home">
    <img src="/brand/logo.png" alt="CRBETS" className={`${className} w-auto object-contain mix-blend-lighten`} />
  </Link>
);

export const DemoBadge = ({ children = "Demo data", testid = "demo-badge" }) => (
  <span data-testid={testid} className="inline-flex items-center gap-1.5 rounded-full border border-cr-gold/40 bg-cr-gold/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-cr-gold">
    {children}
  </span>
);

const STATUS = {
  completed: "bg-cr-success/15 text-cr-success border-cr-success/30",
  verified: "bg-cr-success/15 text-cr-success border-cr-success/30",
  active: "bg-cr-success/15 text-cr-success border-cr-success/30",
  resolved: "bg-cr-success/15 text-cr-success border-cr-success/30",
  pending: "bg-cr-gold/10 text-cr-gold border-cr-gold/30",
  processing: "bg-cr-gold/10 text-cr-gold border-cr-gold/30",
  open: "bg-cr-gold/10 text-cr-gold border-cr-gold/30",
  in_progress: "bg-cr-gold/10 text-cr-gold border-cr-gold/30",
  unverified: "bg-white/5 text-cr-muted border-cr-line",
  cancelled: "bg-white/5 text-cr-muted border-cr-line",
  closed: "bg-white/5 text-cr-muted border-cr-line",
  failed: "bg-cr-danger/15 text-cr-danger border-cr-danger/30",
  rejected: "bg-cr-danger/15 text-cr-danger border-cr-danger/30",
  suspended: "bg-cr-danger/15 text-cr-danger border-cr-danger/30",
};

export const StatusBadge = ({ status, testid }) => (
  <span data-testid={testid || `status-${status}`} className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-semibold capitalize ${STATUS[status] || STATUS.unverified}`}>
    {String(status || "").replace("_", " ")}
  </span>
);

export const TrustBar = ({ className = "" }) => {
  const { config } = useAuth() || {};
  return (
  <div data-testid="trust-bar" className={`grid grid-cols-1 gap-3 border-t border-cr-line py-4 text-sm text-cr-ink/80 sm:grid-cols-3 ${className}`}>
    <div className="flex items-center gap-3 sm:justify-start"><Lock className="h-5 w-5 text-cr-gold" />Encrypted HTTPS connection</div>
    <div className="flex items-center gap-3 sm:justify-center"><Scale className="h-5 w-5 text-cr-gold" />{config?.compliance?.license_text || "Licensed & regulated"}</div>
    <div className="flex items-center gap-3 sm:justify-end">
      <span className="grid h-7 w-7 place-items-center rounded-full border border-cr-ink/60 text-[10px] font-bold">18+</span>Play Responsibly
    </div>
  </div>
  );
};

export const SandboxNotice = ({ className = "" }) => (
  <div data-testid="sandbox-notice" className={`flex items-start gap-2 rounded-lg border border-cr-gold/30 bg-cr-gold/5 px-4 py-3 text-xs text-cr-ink/80 ${className}`}>
    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-cr-gold" />
    <span><b className="text-cr-gold">18+ ONLY.</b> Play responsibly. Set deposit limits, cooling-off or self-exclusion anytime in Settings.</span>
  </div>
);

export const PageTitle = ({ crumbs, title, subtitle, right }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      {crumbs && (
        <div className="mb-3 flex items-center gap-2 text-sm text-cr-ink/70">
          {crumbs.map((c, i) => (
            <span key={c} className={i === crumbs.length - 1 ? "text-cr-gold" : ""}>{c}{i < crumbs.length - 1 && <span className="ml-2 text-cr-muted">›</span>}</span>
          ))}
        </div>
      )}
      <h1 data-testid="page-title" className="text-3xl font-bold sm:text-4xl">{title}</h1>
      {subtitle && <p className="mt-2 text-cr-ink/75">{subtitle}</p>}
    </div>
    {right}
  </div>
);

export const EmptyState = ({ icon: Icon, title, text, action, testid = "empty-state" }) => (
  <div data-testid={testid} className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-cr-line px-6 py-14 text-center">
    {Icon && <Icon className="h-10 w-10 text-cr-gold/70" />}
    <p className="font-display text-lg font-semibold">{title}</p>
    {text && <p className="max-w-md text-sm text-cr-muted">{text}</p>}
    {action}
  </div>
);
